import { FC } from 'react'

import { FileIconSizes } from '@inno-ui/file-icons'
import { IconSizes, PinFullIcon } from '@inno-ui/icons'
import { Spinner, SpinnerColors, SpinnerSizes } from '@inno-ui/spinner'
import { Tooltip, TooltipPlacements } from '@inno-ui/tooltip'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'

import { getItemPrimaryInfo } from '../../../../utils/getItemInfo'
import styles from '../../GridViewItem.module.scss'
import { useAdaptiveTextColor } from '../../hooks'
import { FolderViewItemInfo } from '../Info'
import { ItemIcon } from '../ItemIcon'
import { NameSlot } from '../NameSlot'
import { GridItemContentProps } from './GridItemContent.types'

const noop = () => {}

export const GridItemContent: FC<GridItemContentProps> = ({
  item,
  displayName,
  row,
  col,
  visualStyle,
  isPinned = false,
  subLabel,
  isLoading = false,
  showStillConnectingMessage = false,
  isRenaming = false,
  onRenameSubmit = noop,
  onRenameCancel = noop,
  className,
  ...rest
}) => {
  const { gridItemRef, textColorStyle } = useAdaptiveTextColor(row, col)
  const { t } = useTranslation(undefined, {
    keyPrefix: 'desktop.shortcut_item'
  })
  const resolvedSubLabel = subLabel ?? (isPinned ? t('pinned') : undefined)
  const loadingLabel = showStillConnectingMessage
    ? t('loading_slow')
    : t('loading')

  return (
    <div
      ref={gridItemRef}
      className={cn(styles.gridItem, styles.mediumIcons, className, {
        [styles.featured]: visualStyle === 'featured'
      })}
      style={textColorStyle}
      {...rest}
    >
      <div
        className={cn(styles.iconWrapper, { [styles.isLoading]: isLoading })}
      >
        <ItemIcon item={item} showThumbnail size={FileIconSizes.Size48} />
        {isLoading && (
          <span
            className={styles.loadingIndicator}
            aria-hidden='true'
            data-testid='shortcut-loading-spinner'
          >
            <Spinner size={SpinnerSizes.L} color={SpinnerColors.White} />
          </span>
        )}
        {isPinned && (
          <Tooltip overlay={t('pinned')} placement={TooltipPlacements.TopEnd}>
            <span
              className={styles.pinnedIndicator}
              aria-hidden='true'
              data-testid='pinned-indicator'
            >
              <PinFullIcon size={IconSizes.Size16} color='currentColor' />
            </span>
          </Tooltip>
        )}
      </div>
      <div className={styles.wrapper}>
        <NameSlot
          item={item}
          displayName={displayName}
          isRenaming={isRenaming}
          onSubmit={onRenameSubmit}
          onCancel={onRenameCancel}
        />
        {isLoading ? (
          <p className={styles.subLabel} role='status' aria-live='polite'>
            {loadingLabel}
          </p>
        ) : (
          resolvedSubLabel && (
            <p className={styles.subLabel}>{resolvedSubLabel}</p>
          )
        )}
        <FolderViewItemInfo info={getItemPrimaryInfo(item)} />
      </div>
    </div>
  )
}
